import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { ResponsiveLogo } from '../components/ThemeLogo';
import { Moon, Sun, ArrowLeft } from 'lucide-react';

export default function TermsPage() {
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-background">
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="p-2 hover:bg-muted rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></Link>
              <Link to="/"><ResponsiveLogo className="h-8 w-auto" /></Link>
            </div>
            <button onClick={toggleTheme} className="p-2 rounded-full hover:bg-muted transition-colors">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-heading text-3xl font-bold mb-2">Terms &amp; Conditions</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: August 2026</p>

        {/* Quick nav */}
        <nav className="mb-10 p-4 rounded-xl border border-border bg-card text-sm">
          <p className="font-medium mb-2">Contents</p>
          <div className="grid sm:grid-cols-2 gap-1 text-muted-foreground">
            {[
              '1. Agreement to Terms', '2. Description of Service', '3. User Accounts',
              '4. Acceptable Use', '5. AI & Decision Support Disclaimer', '6. Intellectual Property',
              '7. Fair Use Policy', '8. Subscription & Billing', '9. Termination & Suspension',
              '10. Limitation of Liability', '11. Disclaimer', '12. Governing Law & Jurisdiction',
              '13. Changes to Terms', '14. Contact Information',
            ].map(item => <a key={item} href={`#section-${item.split('.')[0].trim()}`} className="hover:text-odapto-orange transition-colors">{item}</a>)}
          </div>
        </nav>

        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">

          <section id="section-1">
            <h2 className="text-xl font-semibold mb-3">1. Agreement to Terms</h2>
            <p className="text-muted-foreground">
              By accessing or using Odapto, you agree to be bound by these Terms and Conditions and our <Link to="/privacy" className="text-odapto-orange hover:underline">Privacy Policy</Link>. If you do not agree with any part of these terms, you may not access the service.
            </p>
          </section>

          <section id="section-2">
            <h2 className="text-xl font-semibold mb-3">2. Description of Service</h2>
            <p className="text-muted-foreground">
              Odapto is an AI-powered strategic execution platform that helps entrepreneurs, consultants, and business leaders analyze, plan, and execute business strategy. We reserve the right to modify, suspend, or discontinue any aspect of the service at any time.
            </p>
          </section>

          <section id="section-3">
            <h2 className="text-xl font-semibold mb-3">3. User Accounts</h2>
            <p className="text-muted-foreground mb-3">To use certain features, you must create an account. You agree to:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1.5">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your password and not share access credentials</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use</li>
            </ul>
          </section>

          <section id="section-4">
            <h2 className="text-xl font-semibold mb-3">4. Acceptable Use</h2>
            <p className="text-muted-foreground mb-3">You agree not to use Odapto to:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1.5">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on intellectual property rights of others</li>
              <li>Transmit any harmful, threatening, or objectionable content</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the service or servers</li>
              <li>Use the service for any illegal or unauthorized purpose</li>
            </ul>
          </section>

          <section id="section-5">
            <h2 className="text-xl font-semibold mb-3">5. AI &amp; Decision Support Disclaimer</h2>
            <p className="text-muted-foreground mb-3">
              Odapto provides AI-powered insights, recommendations, and strategic analysis to support your thinking and decision-making. These outputs are informational tools, not professional advice.
            </p>
            <p className="text-muted-foreground">
              You remain fully responsible for evaluating, validating, and acting on any information, analysis, or recommendation generated by Odapto. Outcomes cannot be guaranteed. Business decisions are influenced by factors that neither you nor Odapto may know or foresee.
            </p>
          </section>

          <section id="section-6">
            <h2 className="text-xl font-semibold mb-3">6. Intellectual Property</h2>
            <p className="text-muted-foreground">
              The service and its original content, features, and functionality are owned by Odapto and are protected by international copyright, trademark, and other intellectual property laws. You retain ownership of content you create using our service. By posting content, you grant us a non-exclusive, worldwide, royalty-free license to use, store, and display your content solely for providing the service.
            </p>
          </section>

          <section id="section-7" className="p-5 rounded-xl border border-border bg-card">
            <h2 className="text-xl font-semibold mb-3">7. Fair Use Policy</h2>
            <p className="text-muted-foreground mb-3">
              Odapto is designed to help users explore ideas, analyze businesses, solve problems, and support better decision-making. To keep the platform useful and available for everyone, we ask users to use Odapto reasonably and responsibly.
            </p>
            <p className="text-muted-foreground mb-3">
              You may use Odapto and its features for your own business, professional, educational, or personal purposes, subject to your subscription and these Terms.
            </p>
            <p className="text-muted-foreground mb-2 font-medium">You may not:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1.5">
              <li>Use Odapto for unlawful, fraudulent, harmful, or abusive activities.</li>
              <li>Attempt to copy, reverse engineer, reproduce, resell, or commercially exploit Odapto, its platform, methods, templates, prompts, or underlying technology without our permission.</li>
              <li>Use automated systems, bots, scraping tools, or similar methods to access the platform excessively or extract large amounts of content or data.</li>
              <li>Share access credentials or allow others to use your account beyond what your subscription permits.</li>
              <li>Use the service in a way that creates excessive system load, materially increases third-party AI or infrastructure costs, or negatively affects other users.</li>
              <li>Circumvent usage limits, security measures, access controls, or other technical restrictions.</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              Odapto may apply reasonable usage limits where necessary to protect the platform, maintain service quality, control excessive usage or costs, or prevent misuse.
            </p>
            <p className="text-muted-foreground mt-3">
              If we believe use is unreasonable or violates this policy, we may contact the user, temporarily limit certain features, or suspend access in serious or repeated cases.
            </p>
            <p className="text-muted-foreground mt-3">
              We aim to apply this policy reasonably and fairly. Normal use of Odapto for genuine business analysis, strategy, problem-solving, and decision support should not be affected.
            </p>
          </section>

          <section id="section-8">
            <h2 className="text-xl font-semibold mb-3">8. Subscription &amp; Billing</h2>
            <p className="text-muted-foreground mb-3">Some features may require a paid subscription. By subscribing, you agree to:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1.5">
              <li>Pay all fees associated with your subscription</li>
              <li>Provide accurate billing information</li>
              <li>Automatic renewal unless cancelled before the renewal date</li>
            </ul>
            <p className="text-muted-foreground mt-3">During our beta period, all premium features are available free of charge.</p>
          </section>

          <section id="section-9">
            <h2 className="text-xl font-semibold mb-3">9. Termination &amp; Suspension</h2>
            <p className="text-muted-foreground">
              We may terminate or suspend your account immediately, without prior notice, for any reason, including breach of these Terms or the Fair Use Policy. Upon termination, your right to use the service will immediately cease.
            </p>
          </section>

          <section id="section-10">
            <h2 className="text-xl font-semibold mb-3">10. Limitation of Liability</h2>
            <p className="text-muted-foreground">
              To the maximum extent permitted by law, Odapto shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, arising from your use of or inability to use the service.
            </p>
          </section>

          <section id="section-11">
            <h2 className="text-xl font-semibold mb-3">11. Disclaimer</h2>
            <p className="text-muted-foreground">
              The service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without warranties of any kind, either express or implied. We do not warrant that the service will be uninterrupted, timely, secure, or error-free.
            </p>
          </section>

          <section id="section-12" className="p-5 rounded-xl border border-border bg-card">
            <h2 className="text-xl font-semibold mb-3">12. Governing Law &amp; Jurisdiction</h2>
            <p className="text-muted-foreground mb-3">
              These Terms and your use of Odapto are governed by the laws of the Netherlands.
            </p>
            <p className="text-muted-foreground">
              Any dispute arising out of or relating to Odapto, its services, or these Terms will be submitted to the competent courts in Amsterdam, the Netherlands, unless mandatory applicable law requires otherwise.
            </p>
          </section>

          <section id="section-13">
            <h2 className="text-xl font-semibold mb-3">13. Changes to Terms</h2>
            <p className="text-muted-foreground">
              We reserve the right to modify these terms at any time. We will provide notice of significant changes by posting the new Terms on this page. Your continued use of the service after changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section id="section-14">
            <h2 className="text-xl font-semibold mb-3">14. Contact Information</h2>
            <p className="text-muted-foreground">
              For questions about these Terms, please contact us at{' '}
              <a href="mailto:info@odapto.com" className="text-odapto-orange hover:underline">info@odapto.com</a>
            </p>
          </section>

          {/* Legal page links */}
          <div className="pt-6 border-t border-border flex flex-wrap gap-4 text-sm">
            <Link to="/privacy" className="text-odapto-orange hover:underline">Privacy Policy</Link>
            <Link to="/fair-use" className="text-odapto-orange hover:underline">Fair Use Policy</Link>
            <Link to="/contact" className="text-odapto-orange hover:underline">Contact Us</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
